/**
 * The frame every menu screen shares: the screen's name in red over a rule with a glint running
 * along it, a BACK button for mouse and touch, a hint line at the bottom, panels, buttons, and the
 * opening transition (the picture opens from the middle like a tube warming up).
 */

import { textWidth } from '../../render/font';
import { BLACK, RED, WHITE, YELLOW } from '../../render/palette';
import type { Screen } from '../../render/screen';
import type { Rect } from './menuModel';
import { inRect } from './menuModel';
import { CX, ease, FAINT, HEADING, INK, isCalm, LEFT, MUTED, RIGHT, row, SELECTED } from './theme';

/** The header's BACK button: generous, for a thumb. */
export const BACK_RECT: Rect = { x: 0, y: 0, w: 56, h: 24 };
export const HEADER_Y = row(1);
export const RULE_Y = 20;
/** First free line below the header. */
export const CONTENT_TOP = 28;
/** The hint line: character row 34. */
export const FOOTER_Y = row(34);

/** Where the glint is along a rule `width` long at `ms` (its left edge; it runs off the end and rests). Pure. */
export function glintAt(ms: number, width: number): number {
  return Math.floor(((ms * 0.09) % (width + 160)) - 16);
}

/** A red rule from margin to margin; a yellow glint runs along it unless flashing is reduced. */
export function drawRule(screen: Screen, y: number, ms: number, colour = RED): void {
  const width = RIGHT - LEFT;
  screen.fill(LEFT, y, width, 1, colour);
  if (isCalm()) return;
  const g = glintAt(ms, width);
  const x0 = Math.max(0, g);
  const x1 = Math.min(width, g + 16);
  if (x1 <= x0) return;
  screen.fill(LEFT + x0, y, x1 - x0, 1, YELLOW);
  const c0 = Math.max(0, g + 6);
  const c1 = Math.min(width, g + 10);
  if (c1 > c0) screen.fill(LEFT + c0, y, c1 - c0, 1, WHITE);
}

/** The header: `title` centred in red on row 1, the rule under it, and BACK at the left if `back`. */
export function drawHeader(screen: Screen, title: string, ms: number, back: boolean, backHot: boolean): void {
  screen.text(title, CX, HEADER_Y, HEADING, 'center');
  drawRule(screen, RULE_Y, ms);
  if (!back) return;
  const c = backHot ? SELECTED : MUTED;
  screen.text('◄', LEFT, HEADER_Y, c);
  screen.text('BACK', LEFT + 8, HEADER_Y, c);
}

/** (x, y) is on the header's BACK button. */
export function backHit(x: number, y: number): boolean {
  return inRect(BACK_RECT, x, y);
}

/** The hint line at the bottom. */
export function drawFooter(screen: Screen, hint: string, colour = MUTED): void {
  screen.text(hint, CX, FOOTER_Y, colour, 'center');
}

/** Names for the confirm and back buttons, for the input in use. */
export function keyWords(pad: boolean): { ok: string; back: string } {
  return pad ? { ok: 'A', back: 'B' } : { ok: 'ENTER', back: 'ESC' };
}

const REVEAL_MS = 220;

/** The opening transition, drawn over a screen for its first moments. */
export function drawReveal(screen: Screen, ms: number): void {
  if (ms >= REVEAL_MS) return;
  const open = ease(ms, REVEAL_MS);
  const half = Math.round((1 - open) * (screen.height / 2));
  if (half <= 0) return;
  screen.fill(0, 0, screen.width, half, BLACK);
  screen.fill(0, screen.height - half, screen.width, half, BLACK);
  screen.fill(0, half, screen.width, 1, WHITE);
  screen.fill(0, screen.height - half - 1, screen.width, 1, WHITE);
}

/** A panel: black inside, a dark frame with bright corners. */
export function drawPanel(screen: Screen, r: Rect, border = FAINT, corner = MUTED): void {
  screen.fill(r.x, r.y, r.w, r.h, BLACK);
  screen.box(r.x, r.y, r.w, r.h, border);
  const right = r.x + r.w - 2;
  const bottom = r.y + r.h - 2;
  for (const [cx, cy] of [[r.x, r.y], [right, r.y], [r.x, bottom], [right, bottom]] as const) {
    screen.fill(cx, cy, 2, 2, corner);
  }
}

export type ButtonState = 'idle' | 'selected' | 'disabled';

/** A button: its label centred in a frame; yellow, with a nudging arrow, when selected. */
export function drawButton(screen: Screen, r: Rect, label: string, state: ButtonState, ms: number): void {
  const on = state === 'selected';
  screen.fill(r.x, r.y, r.w, r.h, BLACK);
  screen.box(r.x, r.y, r.w, r.h, on ? SELECTED : FAINT);
  const ty = r.y + Math.floor((r.h - 8) / 2) + 1;
  const colour = state === 'disabled' ? FAINT : on ? SELECTED : INK;
  screen.text(label, r.x + Math.floor(r.w / 2), ty, colour, 'center');
  if (on && r.w >= textWidth(label) + 28) screen.text('►', r.x + 4 + nudge(ms), ty, SELECTED);
}

/** 0-2 pixels: the little push a selection marker gives. Pure. */
export function nudge(ms: number): number {
  return Math.round(Math.max(0, Math.sin(ms / 140)) * 2);
}
